import { assert } from '../../asserts'
import { getAudioStream } from '../../permissions'
import {
  AudioStreamBlobEvent,
  AudioStreamEvent,
  AudioStreamEventTarget
} from './AudioStreamEventTarget'

export interface AudioStreamConfig {
  timeslice: number
  audioBitsPerSecond: number
}

export class AudioStreamManager extends AudioStreamEventTarget {
  #config: AudioStreamConfig
  #mediaStreamPromise: Promise<MediaStream> | null = null
  #mediaStream: MediaStream | null = null
  #mediaRecorder: MediaRecorder | null = null
  #isCanceled = false
  #abortController = new AbortController()

  constructor(config: AudioStreamConfig) {
    super()
    this.#config = config
  }

  get mediaRecorder() {
    assert(this.#mediaRecorder !== null, 'this.#mediaRecorder not defined yet')
    return this.#mediaRecorder
  }

  get mediaStream() {
    assert(this.#mediaStream !== null, 'this.#mediaStream not defined yet')
    return this.#mediaStream
  }

  get mediaStreamPromise() {
    assert(
      this.#mediaStreamPromise !== null,
      'this.#mediaStreamPromise not defined yet'
    )
    return this.#mediaStreamPromise
  }

  async start() {
    this.#mediaStreamPromise = getAudioStream()
    this.#mediaStream = await this.#mediaStreamPromise
    this.#mediaRecorder = new MediaRecorder(this.#mediaStream, {
      audioBitsPerSecond: this.#config.audioBitsPerSecond
    })

    this.#mediaRecorder.addEventListener('start', this.#handleStart, {
      once: true,
      signal: this.#abortController.signal
    })
    this.#mediaRecorder.addEventListener('stop', this.#handleStop, {
      once: true,
      signal: this.#abortController.signal
    })
    this.#mediaRecorder.addEventListener('pause', this.#handlePause, {
      signal: this.#abortController.signal
    })
    this.#mediaRecorder.addEventListener('resume', this.#handleResume, {
      signal: this.#abortController.signal
    })
    this.#mediaRecorder.addEventListener('dataavailable', this.#handleData, {
      signal: this.#abortController.signal
    })
    this.#mediaRecorder.start(this.#config.timeslice)
  }

  stop() {
    this.mediaRecorder.stop()
  }

  cancel() {
    this.#isCanceled = true
    this.mediaRecorder.stop()
  }

  pause() {
    this.mediaRecorder.pause()
  }

  resume() {
    this.mediaRecorder.resume()
  }

  async teardown() {
    try {
      await this.mediaStreamPromise
      const [track] = this.mediaStream.getTracks()
      if (track) {
        track.stop()
      }
      this.#abortController.abort('teardown')
      this.#mediaStream = null
      this.#mediaStreamPromise = null
      this.#mediaRecorder = null
    } catch {}
  }

  #handleStart = () => {
    this.dispatchEvent(new AudioStreamEvent('start'))
  }

  #handleStop = () => {
    this.teardown()
  }

  #handlePause = () => {
    this.dispatchEvent(new AudioStreamEvent('pause'))
  }

  #handleResume = () => {
    this.dispatchEvent(new AudioStreamEvent('resume'))
  }

  #handleData = (blobEvent: BlobEvent) => {
    this.dispatchEvent(new AudioStreamBlobEvent('dataavailable', blobEvent))
  }
}
